import type { ProjectSyncField } from '@qms360/shared';
import { apiFetch } from './api.js';

/** The Data Sync Cockpit (spec §13.4). */

export interface SyncJob {
  id: string;
  jobKey: string;
  trigger: 'schedule' | 'manual' | 'retry';
  outcome: 'running' | 'succeeded' | 'partial' | 'failed' | 'queued';
  dryRun: boolean;
  fieldMappingVersion: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  recordsRead: number;
  recordsWritten: number;
  recordsFailed: number;
  summary: {
    dryRun?: boolean;
    created?: number;
    updated?: number;
    unchanged?: number;
    failed?: number;
    error?: string;
    missingAtSource?: { code: string; name: string; externalId: string | null }[];
    localOnly?: number;
    mappingVersion?: number;
  };
  createdAt: string;
}

export interface SyncError {
  id: string;
  jobId: string;
  recordKey: string | null;
  record: Record<string, unknown>;
  error: string;
  status: 'open' | 'fixed' | 'ignored';
  attempts: number;
  resolutionNote: string | null;
  resolvedAt: string | null;
  updatedAt: string;
}

export const fetchJobs = (params: { jobKey?: string; before?: string | null } = {}) => {
  const query = new URLSearchParams();
  if (params.jobKey) query.set('jobKey', params.jobKey);
  if (params.before) query.set('before', params.before);
  const suffix = query.toString();
  return apiFetch<{ jobs: SyncJob[]; nextCursor: string | null; canRun: boolean }>(
    `/admin/sync/jobs${suffix ? `?${suffix}` : ''}`,
  );
};

export const runProjectSync = (dryRun: boolean) =>
  apiFetch<{ job: SyncJob }>('/admin/sync/project-sync/run', { method: 'POST', body: JSON.stringify({ dryRun }) });

export const fetchSyncErrors = (status: 'open' | 'fixed' | 'ignored' | '' = 'open') =>
  apiFetch<{ errors: SyncError[]; canResolve: boolean }>(`/admin/sync/errors${status ? `?status=${status}` : ''}`);

export const retrySyncError = (id: string) =>
  apiFetch<{ error: SyncError }>(`/admin/sync/errors/${id}/retry`, { method: 'POST' });

export const ignoreSyncError = (id: string, reason: string) =>
  apiFetch<{ error: SyncError }>(`/admin/sync/errors/${id}/ignore`, { method: 'POST', body: JSON.stringify({ reason }) });

export interface Reconciliation {
  lastRun: SyncJob | null;
  openErrors: number;
  localOnly: number;
}

export const fetchReconciliation = () => apiFetch<Reconciliation>('/admin/sync/reconciliation');

export interface MappingView {
  current: { version: number; mapping: Record<string, string> };
  versions: { version: number; mapping: Record<string, string>; note: string | null; createdAt: string }[];
  fields: readonly { key: ProjectSyncField; label: string; required: boolean }[];
  defaultMapping: Record<string, string>;
  canChange: boolean;
}

export const fetchMapping = () => apiFetch<MappingView>('/admin/sync/mapping');

export const saveMapping = (mapping: Record<string, string>, note: string | null) =>
  apiFetch<MappingView>('/admin/sync/mapping', { method: 'PUT', body: JSON.stringify({ mapping, note }) });
